import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { BitmapText, Container, Sprite } from '@pixi/react';
import * as PIXI from 'pixi.js';
import { useMemo } from 'react';
import { useAppSelector } from '../../../../redux/store';
import { PLAYER_BUBBLE_CARD_BACKGROUND_COLOR_NUM, PLAYER_BUBBLE_CARD_BORDER_COLOR_NUM } from '../../../../styles/colors';
import { PLAYER_BUBBLE_CARD_RANK_LETTER_SPACING } from '../../../../styles/pixiStyles';
import { CARD_RANK_LABELS } from '../../../../types/cardTypes';
import { CARD_ASSET_URLS } from '../../../../utils/loaderUtils';
import { getBitmapTextDimensions } from '../../../../utils/textMetricsUtils';
import { isTestEnvironment } from '../../../../utils/utils';
import { Rect } from '../../pixiComponents/rect';
const MAX_DISTANCE_BETWEEN_RANK_AND_SUIT = 18;
const HALF_MAX_DISTANCE_BETWEEN_RANK_AND_SUIT = Math.round(MAX_DISTANCE_BETWEEN_RANK_AND_SUIT / 2);
function getCardSuitAssetUrl(card, isFourColorDeckEnabled) {
    switch (card.suitChar) {
        case 'c': {
            return CARD_ASSET_URLS[isFourColorDeckEnabled ? 'club-icon-4color-deck' : 'club-icon'];
        }
        case 'd': {
            return CARD_ASSET_URLS[isFourColorDeckEnabled ? 'diamond-icon-4color-deck' : 'diamond-icon'];
        }
        case 'h': {
            return CARD_ASSET_URLS['heart-icon'];
        }
        case 's': {
            return CARD_ASSET_URLS['spade-icon'];
        }
    }
}
// TODO: Consider redoing the spacing logic to better handle differences in rank/suit dimensions
// (for example, the '10' is wider than other ranks and does not appear positioned quite right).
// A better approach might be to space rank and suit based on the intended space between their
// edges and not their centers.
export const PlayerBubbleCardDisplay = ({ playerBubble, card, x, y, w, h }) => {
    const isFourColorDeckEnabled = useAppSelector((state) => state.selfData.isFourColorDeckEnabled);
    const rankText = CARD_RANK_LABELS[card.rankChar];
    const rankTextDimensions = useMemo(() => getBitmapTextDimensions(rankText, 'player-bubble-card-rank', {
        letterSpacing: PLAYER_BUBBLE_CARD_RANK_LETTER_SPACING
    }), [rankText]);
    const rankCenterMinX = Math.round(w / 3);
    const suitCenterMaxX = Math.round((w * 2) / 3);
    const centerX = Math.round(w / 2);
    const centerY = Math.round(h / 2);
    const rankCenterX = Math.max(centerX - HALF_MAX_DISTANCE_BETWEEN_RANK_AND_SUIT, rankCenterMinX);
    const suitCenterX = Math.min(centerX + HALF_MAX_DISTANCE_BETWEEN_RANK_AND_SUIT, suitCenterMaxX);
    const suitScale = 0.5;
    const suitTexture = PIXI.Texture.from(getCardSuitAssetUrl(card, isFourColorDeckEnabled));
    const suitX = suitCenterX - Math.round((suitTexture.width / 2) * suitScale);
    const suitY = centerY - Math.round((suitTexture.height / 2) * suitScale);
    const rankTextX = Math.round(rankCenterX - rankTextDimensions.w / 2);
    const rankTextY = Math.round(centerY - rankTextDimensions.h / 2);
    const pixiData = isTestEnvironment()
        ? {
            type: 'player-bubble-card',
            bubbleId: playerBubble.id,
            seatIndex: playerBubble.seatIndex,
            cardShorthand: card.shorthand
        }
        : null;
    return (_jsxs(Container, { x: x, y: y, "pixi-data": pixiData, children: [_jsx(Rect, { x: 0, y: 0, w: w, h: h, radius: 6, fillColor: PLAYER_BUBBLE_CARD_BACKGROUND_COLOR_NUM, lineWidth: 2, lineColor: PLAYER_BUBBLE_CARD_BORDER_COLOR_NUM }), _jsx(BitmapText, { text: rankText, style: { fontName: 'player-bubble-card-rank', letterSpacing: PLAYER_BUBBLE_CARD_RANK_LETTER_SPACING }, x: rankTextX, y: rankTextY }), _jsx(Sprite, { texture: suitTexture, x: suitX, y: suitY, scale: suitScale })] }));
};
